import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { en, type Dictionary } from "./locales/en";
import { zhHans, zhHant, pa, ko, fa, ar } from "./locales/translations";
import { LOCALES as LOCALE_DEFINITIONS, type LocaleCode } from "./locale-config";

export {
  localizePath,
  localeToPathSegment,
  parseLocalizedPath,
  resolveLegacyRedirect,
  switchLocalePath,
  localizedRoutePath,
  type ParsedLocalizedPath,
  type RouteId,
} from "./routing";
export type { LocaleCode } from "./locale-config";
export { LOCALES as LOCALE_REGISTRY, DEFAULT_LOCALE } from "./locale-config";

const STORAGE_KEY = "revolin.locale";

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function deepMerge<T>(base: T, override: unknown): T {
  if (!isPlainObject(override) || !isPlainObject(base)) {
    return override === undefined ? base : (override as T);
  }
  const result: Record<string, unknown> = { ...base };
  for (const key of Object.keys(override)) {
    const o = override[key];
    if (o === undefined) continue;
    result[key] = isPlainObject(o) ? deepMerge((base as Record<string, unknown>)[key], o) : o;
  }
  return result as T;
}

/** UI locale entries wired to route locale keys. */
export const LOCALES = [
  { code: "en-ca" as const, label: "English", short: "EN", dir: "ltr" as const, dict: en },
  {
    code: "zh-cn" as const,
    label: "简体中文",
    short: "简",
    dir: "ltr" as const,
    dict: zhHans,
  },
  {
    code: "zh-tw" as const,
    label: "繁體中文",
    short: "繁",
    dir: "ltr" as const,
    dict: zhHant,
  },
  { code: "pa-in" as const, label: "ਪੰਜਾਬੀ", short: "ਪੰ", dir: "ltr" as const, dict: pa },
  { code: "ko-kr" as const, label: "한국어", short: "KO", dir: "ltr" as const, dict: ko },
  { code: "fa-ir" as const, label: "فارسی", short: "فا", dir: "rtl" as const, dict: fa },
  { code: "ar-sa" as const, label: "العربية", short: "ع", dir: "rtl" as const, dict: ar },
] as const;

const LEGACY_STORAGE_MAP: Record<string, LocaleCode> = {
  en: "en-ca",
  "zh-Hans": "zh-cn",
  "zh-Hant": "zh-tw",
  pa: "pa-in",
  ko: "ko-kr",
  fa: "fa-ir",
  ar: "ar-sa",
};

function normalizeStoredLocale(value: string | null): LocaleCode | null {
  if (!value) return null;
  if (LOCALES.some((l) => l.code === value)) return value as LocaleCode;
  return LEGACY_STORAGE_MAP[value] ?? null;
}

type I18nValue = {
  locale: LocaleCode;
  setLocale: (code: LocaleCode) => void;
  dir: "ltr" | "rtl";
  t: Dictionary;
};

const I18nContext = createContext<I18nValue | null>(null);

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  initialLocale?: LocaleCode;
}) {
  const [locale, setLocaleState] = useState<LocaleCode>(initialLocale ?? "en-ca");

  useEffect(() => {
    if (initialLocale) {
      setLocaleState(initialLocale);
      try {
        window.localStorage.setItem(STORAGE_KEY, initialLocale);
      } catch {
        /* storage unavailable */
      }
      return;
    }

    try {
      const stored = normalizeStoredLocale(window.localStorage.getItem(STORAGE_KEY));
      if (stored) setLocaleState(stored);
    } catch {
      /* storage unavailable */
    }
  }, [initialLocale]);

  const setLocale = useCallback((code: LocaleCode) => {
    setLocaleState(code);
    try {
      window.localStorage.setItem(STORAGE_KEY, code);
    } catch {
      /* storage unavailable */
    }
  }, []);

  const value = useMemo<I18nValue>(() => {
    const entry = LOCALES.find((l) => l.code === locale) ?? LOCALES[0]!;
    const localeDef = LOCALE_DEFINITIONS.find((l) => l.routeLocaleKey === locale);
    return {
      locale,
      setLocale,
      dir: localeDef?.dir ?? entry.dir,
      t: deepMerge(en, entry.dict) as Dictionary,
    };
  }, [locale, setLocale]);

  useEffect(() => {
    document.documentElement.lang = LOCALE_DEFINITIONS.find(
      (l) => l.routeLocaleKey === value.locale,
    )!.bcp47;
    document.documentElement.dir = value.dir;
  }, [value.locale, value.dir]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}
